import { useMemo } from 'react';
import { createSearchParams, Link, useNavigate } from 'react-router-dom';
import { demeterMarketIndicatorsApi } from '../../../Apis/Apis';
import scssVariables from '../../../Config.module.scss';
import { DemeterFeatureType, DemeterPermissionType, MarketIndicatorSummaryModel, RunValueMatrixRequest } from '../../../Generated/Raven-Demeter';
import NavigationRoutes from '../../../Layouts/NavigationRoutes';
import { SearchParameters } from '../../../Redux/Slices/SystemSlice';
import useApi from '../../Apis/Hooks/useApiHook';
import { chartColors } from '../../Components/Charts/ChartDefinitions';
import chartService from '../../Components/Charts/ChartService';
import TableSkeleton from '../../Components/Layout/TableSkeleton';
import OverflowCellWithTooltip from '../../Components/Tables/OverflowCellWithTooltip/OverflowCellWithTooltip';
import useFeatureFlag from '../../Services/FeatureFlags/useFeatureFlagHook';
import formattingService from '../../Services/Formatting/FormattingService';
import useLanguage from '../../Services/Language/useLanguageHook';
import usePermission from '../../Services/Permissions/usePermissionHook';
import styles from './Dashboard.module.scss';

const minimumCellValueForWhiteText = 10;
const maximumCellValueForWhiteText = 90;
const minimumAllowedDecileValue = 0;
const maximumAllowedDecileValue = 100;

type CellType = 'commodity' | 'split' | 'positiveOrNegativeText' | 'medianBackground' | 'outlook';
type QuarterYearValue = { quarter: number; year: number };

interface IDashboardCommoditySummaryTableProps {
    useDynamicHeight?: boolean;
}

const DashboardCommoditySummaryTable: React.FC<IDashboardCommoditySummaryTableProps> = (props: IDashboardCommoditySummaryTableProps) => {
    const [translations, translate] = useLanguage();
    const userHasHedgeMonitorProPermissions = useFeatureFlag(DemeterFeatureType.HedgeMonitorPro) && usePermission(DemeterPermissionType.HedgeMonitorPro);
    const navigate = useNavigate();

    // Text hooks.
    const priceValueMarixTitleString = useMemo(() => translations.dashboard.headings.priceValueMatrix, [translations]);
    const commoditySummaryTableFootNote = useMemo(() => translations.dashboard.text.commoditySummaryTableFootnote, [translations]);

    // Data hooks.
    const [, , listMarketIndicatorSummariesResponse] = useApi(() => demeterMarketIndicatorsApi.listMarketIndicatorSummaries());

    // Table hooks.
    const commonQuarterDeciles = useMemo(() => {
        if (!listMarketIndicatorSummariesResponse) {
            return null;
        }

        const quarterDeciles = listMarketIndicatorSummariesResponse.rows?.filter((x) => x.quarterDeciles.length !== 0).map((x) => x.quarterDeciles);

        return quarterDeciles?.map((x) => x.filter((y) => quarterDeciles.every((z) => z.find((a) => a?.quarter === y?.quarter && a?.year === y?.year))))![0]!;
    }, [listMarketIndicatorSummariesResponse]);

    const tableHeaders = useMemo(() => {
        if (!commonQuarterDeciles) {
            return null;
        }

        const tableColumns = [
            {
                label: translations.words.commodity,
                value: 'commodity',
                cellType: 'commodity',
            },
            {
                label: `% ${translations.text.monthOverMonth}`,
                value: 'monthOverMonthChangePercent',
                cellType: 'positiveOrNegativeText',
            },
            {
                label: `% ${translations.words.shortYearOverYear}`,
                value: 'rollingYearChangePercent',
                cellType: 'positiveOrNegativeText',
            },
            {
                label: translations.hedgeMonitorPro.text.fiftiethDecile,
                value: 'annual',
                cellType: 'medianBackground',
            },
            {
                label: translations.words.spot,
                value: 'currentPrice',
                cellType: 'split',
            },
            ...commonQuarterDeciles.map((decile) => ({
                label: `${translations.words.quarterShort} ${decile.quarter}, ${decile.year}`,
                value: { quarter: decile.quarter, year: decile.year },
                cellType: 'split',
            })),
        ] as { label: string; value: keyof MarketIndicatorSummaryModel | QuarterYearValue; cellType?: CellType }[];

        if (userHasHedgeMonitorProPermissions) {
            tableColumns.push({
                label: translations.marketIndicators.hedgeMontiorPro,
                value: 'outlook',
                cellType: 'outlook',
            });
        }

        return tableColumns;
    }, [translations, commonQuarterDeciles]);

    const tableDataCells = useMemo(() => {
        if (!tableHeaders) {
            return null;
        }

        return listMarketIndicatorSummariesResponse?.rows
            ?.filter((x) => !x.usePrices)
            .map((x) => (
                <div key={x.marketIndicatorGuid} className={styles.commodity_summary_table_row}>
                    {tableHeaders.map((y, index) => {
                        const selectedValue = x[y.value as keyof typeof x]! as number;

                        const selectedTermClassification = x.quarterDeciles.find(
                            (z) => z?.quarter === (y.value as QuarterYearValue)?.quarter && z?.year === (y.value as QuarterYearValue)?.year,
                        );

                        let currencyAndUnitOfMeasureString = '';

                        if (x.currency && x.unitOfMeasure) {
                            currencyAndUnitOfMeasureString = `${translations.currency[x.currency]} / ${
                                translations.unitOfMeasure[`Short${x.unitOfMeasure}`] ?? ''
                            }`;
                        } else if (x.unitOfMeasure) {
                            currencyAndUnitOfMeasureString = translations.unitOfMeasure[`Short${x.unitOfMeasure}`] ?? '';
                        } else if (x.currency) {
                            currencyAndUnitOfMeasureString = translations.currency[x.currency];
                        }

                        if (index === 0) {
                            return (
                                <div key={`${x.marketIndicatorGuid}-${y.label}`} className={styles.commodity_summary_table_cell_extra_large}>
                                    <OverflowCellWithTooltip
                                        value={`${translate(x.displayName ?? '')} ${currencyAndUnitOfMeasureString}`}
                                        nonTooltipOverrideValue={
                                            <div className={styles.commodity_summary_table_no_wrap_flex}>
                                                <div className={styles.commodity_summary_table_display_name_text}>{translate(x.displayName ?? '')}</div>
                                                <div className={styles.commodity_summary_table_sub_text}>{currencyAndUnitOfMeasureString}</div>
                                            </div>
                                        }
                                    />
                                </div>
                            );
                        }

                        if (y.cellType === 'outlook') {
                            return (
                                <div className={styles.commodity_summary_table_outlook_cell}>
                                    <div
                                        style={{
                                            color: scssVariables.mainTextColor,
                                            background: chartService.getOutlookColor(x.hedgeMonitorProOutlook!),
                                        }}
                                        className={styles.commodity_summary_table_outlook}
                                    >
                                        <button
                                            className={styles.commodity_summary_table_outlook_button}
                                            type="button"
                                            onClick={() => navigateToHedgeMonitorPro(x.marketIndicatorGuid)}
                                        >
                                            <OverflowCellWithTooltip
                                                value={x.hedgeMonitorProOutlook ? translations.marketIndicatorOutlook[x.hedgeMonitorProOutlook] : ''}
                                            />
                                        </button>
                                    </div>
                                </div>
                            );
                        }

                        if (y.cellType === 'positiveOrNegativeText') {
                            return (
                                <div
                                    key={`${x.marketIndicatorGuid}-${y.label}`}
                                    className={styles.commodity_summary_table_cell}
                                    style={{ color: selectedValue > 0 ? scssVariables.tableTextPositive : scssVariables.tableTextNegativeAlternative }}
                                >
                                    <OverflowCellWithTooltip value={`${formattingService.formatPercentNumber(selectedValue)}`} />
                                </div>
                            );
                        }

                        if (y.cellType === 'medianBackground') {
                            const valueMatrixUrlRequest: SearchParameters = {
                                tab: 'ValueMatrix',
                                market: x.market!,
                                stripType: x.runValueMatrixRequest.priceType === 'Futures' ? 'firstContractMonth' : 'spot',
                            };

                            Object.keys(x.runValueMatrixRequest).forEach((z) => {
                                if (!x.runValueMatrixRequest[z as keyof RunValueMatrixRequest]) {
                                    return;
                                }

                                valueMatrixUrlRequest[z] = x.runValueMatrixRequest[z as keyof RunValueMatrixRequest]!.toString();
                            });

                            const newSearchParameters = createSearchParams(valueMatrixUrlRequest).toString();

                            return (
                                <div
                                    key={`${x.marketIndicatorGuid}-${y.label}`}
                                    className={styles.commodity_summary_table_median}
                                    style={{ color: scssVariables.plainWhite, backgroundColor: scssVariables.decileMedianLine }}
                                >
                                    <Link to={`${NavigationRoutes.Calculators}?${newSearchParameters}`}>
                                        <OverflowCellWithTooltip value={`${x?.median}`} />
                                    </Link>
                                </div>
                            );
                        }

                        if (y.cellType === 'split') {
                            const selectedDecile = y.value === 'currentPrice' ? x.spotDecile.decile : selectedTermClassification?.decile ?? 0;
                            const currentDecileColor = chartColors.priceDecileColorsRuleSetTwo.find(
                                (z) => (z.decileRank - 1) * 10 <= (selectedDecile! ?? 0),
                            )?.color;

                            const getCurrentDecileString = () => {
                                if (Math.round(selectedDecile) === minimumAllowedDecileValue) {
                                    return translations.words.minimum;
                                }

                                if (Math.round(selectedDecile) === maximumAllowedDecileValue) {
                                    return translations.words.maximum;
                                }

                                return formattingService.formatPriceNumber(Math.round(selectedDecile));
                            };

                            const cellValue = y.value === 'currentPrice' ? x.spotDecile.value : selectedTermClassification?.value;

                            return (
                                <div key={`${x.marketIndicatorGuid}-${y.label}`} className={styles.commodity_summary_table_cell}>
                                    <div className={styles.commodity_summary_table_cell_split_value_large}>
                                        <OverflowCellWithTooltip
                                            value={formattingService.formatNumber(cellValue, {
                                                precision: formattingService.getDisplayDecimalPlacesMinimum(cellValue),
                                            })}
                                        />
                                    </div>
                                    <div
                                        className={styles.commodity_summary_table_cell_split_value_small}
                                        style={{
                                            color:
                                                selectedDecile < minimumCellValueForWhiteText || selectedDecile > maximumCellValueForWhiteText
                                                    ? scssVariables.plainWhite
                                                    : scssVariables.mainTextColor,
                                            backgroundColor: currentDecileColor,
                                        }}
                                    >
                                        <div>{getCurrentDecileString()}</div>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <div key={`${x.marketIndicatorGuid}-${y.label}`} className={styles.commodity_summary_table_cell}>
                                <OverflowCellWithTooltip value={`${selectedValue}`} />
                            </div>
                        );
                    })}
                </div>
            ));
    }, [listMarketIndicatorSummariesResponse, tableHeaders]);

    const isLoading = !tableHeaders || !tableDataCells;

    const lengthOfSpacer = userHasHedgeMonitorProPermissions ? 5 : 4;

    const containerClassName = props.useDynamicHeight ? styles.commodity_summary_table_container_dynamic_height : styles.commodity_summary_table_container;

    const navigateToHedgeMonitorPro = (marketIndicatorGuid: string) => {
        navigate({
            pathname: NavigationRoutes.HedgeMonitorPro,
            search: createSearchParams({ marketIndicatorGuid }).toString(),
        });
    };

    return isLoading ? (
        <TableSkeleton />
    ) : (
        <div className={containerClassName}>
            <div className={styles.commodity_summary_table_header_area}>
                <div className={styles.commodity_summary_table_top_title_spacer} />
                <div className={styles.commodity_summary_table_top_title} style={{ flex: `${tableHeaders.length - lengthOfSpacer}` }}>
                    {priceValueMarixTitleString}
                </div>
                {userHasHedgeMonitorProPermissions && <div className={styles.commodity_summary_table_top_title_spacer_end} />}
            </div>
            <div>
                <div className={styles.commodity_summary_table_row}>
                    {tableHeaders.map((x, index) => {
                        if (index === 0) {
                            return (
                                <div key={`header-${x.label}`} className={styles.commodity_summary_table_header_item_extra_large}>
                                    {x.label}
                                </div>
                            );
                        }

                        if (x.value === 'outlook') {
                            return (
                                <div key={`header-${x.label}`} className={styles.commodity_summary_table_header_item_outlook}>
                                    <span className={styles.commodity_summary_table_header_item_outlook_text}>{x.label}</span>
                                </div>
                            );
                        }

                        return (
                            <div key={`header-${x.label}`} className={styles.commodity_summary_table_header_item}>
                                {x.label}
                            </div>
                        );
                    })}
                </div>
                {tableDataCells}
                <p className={styles.commodity_summary_table_footnote}>
                    {commoditySummaryTableFootNote}
                    <Link to={`${NavigationRoutes.Calculators}`}>{translations.calculators.text.valueMatrix}</Link>.
                </p>
            </div>
        </div>
    );
};

export default DashboardCommoditySummaryTable;
